import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import {
  Button,
  Container,
  TabContent,
  TabPane,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import {
  APIgetExports,
  APIcreateExport,
  APIgetExport,
} from '../../api/Export';
import { APIgetReportsSettings } from '../../api/Reports';

import AdminReportsFilterDates from '../../components/Admin/Reports/FilterDates';
import AdminExportHeader from '../../components/Admin/Export/Header';
import AdminExportsFooter from '../../components/Admin/Export/Footer';
import AdminExportsList from '../../components/Admin/Export/List';
import ProcessingSpinner from '../../components/ProcessingSpinner';
import ErrorBoundary from '../../components/ErrorBoundary';
import Loading from '../../components/Loading';

import { getVisibleItemsText } from '../../helpers';
import { addFlashMessage } from '../../helpers/FlashMessages';
import { handleComponentParams, handleURLParams } from '../../helpers/URLParams';
import { getUserSetting } from '../../helpers/Settings';

const AdminExport = ({ history, location }) => {
  const [ready, setReady] = useState(false);
  const [exports, setExports] = useState([]);
  const [totalExports, setTotalExports] = useState([]);
  const [dateFrom, setDateFrom] = useState(moment().subtract(30, 'days'));
  const [dateTo, setDateTo] = useState(moment());
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [exportsPage, setExportsPage] = useState(1);
  const [exportsPerPage, setExportsPerPage] = useState(getUserSetting('admin_per_page') || 20);
  const [exportsOrderBy, setExportsOrderBy] = useState('created_at');
  const [exportsOrder, setExportsOrder] = useState('desc');
  const [exportsLoading, setExportsLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const intl = useIntl();

  const loadExports = useCallback((silent = false) => {
    if (!silent) {
      setExportsLoading(true);
    }
    return APIgetExports({
      page: exportsPage,
      orderby: exportsOrderBy,
      order: exportsOrder,
      per_page: exportsPerPage,
    }).then((response) => {
      setExports(response.data);
      setTotalExports(response.meta.total);
      setExportsLoading(false);
    });
  }, [exportsPage, exportsPerPage, exportsOrderBy, exportsOrder]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (exports.filter(ex => (['pending', 'processing'].includes(ex.status))).length > 0) {
        loadExports(true);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [exports, loadExports]);

  useEffect(() => {
    handleComponentParams(
      true,
      ['page', 'perPage', 'orderBy', 'order'],
      null,
      [setExportsPage, setExportsPerPage, setExportsOrderBy, setExportsOrder],
      () => {},
    );

    setReady(true);

    const queryParams = new URLSearchParams(location.search);

    if (!queryParams.get('orderBy')) {
      handleURLParams({ orderBy: exportsOrderBy }, history);
    }

    if (!queryParams.get('order')) {
      handleURLParams({ order: exportsOrder }, history);
    }
  }, [exportsOrder, exportsOrderBy, history, location.search]);

  useEffect(() => {
    if (!ready) return;
    loadExports();
  }, [loadExports, ready]);

  useEffect(() => {
    const fetchReportsSettings = () => {
      setSettingsLoading(true);
      APIgetReportsSettings().then((response) => {
        setSettings(response);
        setSettingsLoading(false);
      });
    };
    fetchReportsSettings();
  }, []);

  const createExport = () => {
    setSaving(true);
    APIcreateExport({
      start_date: dateFrom.format('YYYY-MM-DD'),
      end_date: dateTo.format('YYYY-MM-DD'),
    }).then((response) => {
      if (response && response.uuid) {
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'Your export is being processed and will be ready to download shortly' }),
          duration: 5000,
        });
        setExportsPage(1);
        loadExports(true);
      } else {
        addFlashMessage({
          type: 'danger',
          text: intl.formatMessage({ defaultMessage: 'Something went wrong' }),
        });
      }
      setSaving(false);
    });
  };

  const handleChangeOrder = (orderBy, order) => {
    handleURLParams({ orderBy, order }, history);

    setExportsOrderBy(orderBy);
    setExportsOrder(order);
    setExportsPage(1);
  };

  const handleChangePage = (page) => {
    handleURLParams({ page }, history);
    setExportsPage(page);
  };

  const handleChangePerPage = (perPage) => {
    handleURLParams({ perPage, page: 1 }, history);
    setExportsPage(1);
    setExportsPerPage(perPage);
  };

  const handleDownload = (exportUuid) => {
    APIgetExport(exportUuid).then(() => {

    });
  };

  return (
    <div className="main-content export-content">
      <Container fluid>
        <header className="page-header">
          <h3>
            {intl.formatMessage({ defaultMessage: 'Export' })}
          </h3>
        </header>
        <div>
          {(exportsLoading || settingsLoading) ? (
            <Loading />
          ) : (
            <TabContent className="settings-tab-content export">
              <TabPane className="active">
                <div className="page-header">
                  <div className="report-filters-wrapper form-inline">
                    <div className="report-filters-control col-12 mt-3">
                      <AdminReportsFilterDates
                        settings={settings}
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        setDateFrom={setDateFrom}
                        setDateTo={setDateTo}
                      />
                      <div className="report-buttons-wrapper">
                        <Button
                          disabled={saving}
                          onClick={createExport}
                        >
                          <ProcessingSpinner show={saving} />
                          {intl.formatMessage({ defaultMessage: 'Export' })}
                        </Button>
                      </div>
                    </div>
                  </div>
                  <AdminExportHeader
                    exportsTotal={totalExports}
                    exportsVisible={getVisibleItemsText(exportsPage, exportsPerPage, totalExports)}
                    loading={exportsLoading}
                  />
                </div>
                <ErrorBoundary>
                  <AdminExportsList
                    exports={exports}
                    orderBy={exportsOrderBy}
                    order={exportsOrder}
                    loadExports={loadExports}
                    loading={exportsLoading}
                    handleChangeOrder={handleChangeOrder}
                    handleDownload={handleDownload}
                  />
                </ErrorBoundary>
                <ErrorBoundary>
                  <AdminExportsFooter
                    exportsTotal={totalExports}
                    page={exportsPage}
                    perPage={exportsPerPage}
                    handleChangePage={handleChangePage}
                    handleChangePerPage={handleChangePerPage}
                  />
                </ErrorBoundary>
              </TabPane>
            </TabContent>
          )}
        </div>
      </Container>
    </div>
  );
};

AdminExport.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

export default withRouter(AdminExport);
